import { ScrollView, Text, View } from "react-native";

import { BrowsePill } from "@/components/browse/BrowsePill";
import type { PublicLiveTable } from "@/hooks/usePublicBrowse";

/**
 * Which of a vendor's live tables a customer is browsing (ADR-0012, ADR-0138).
 *
 * A vendor running two shows at once is two staff each operating their own Event on one tenant —
 * a designed capability the storefront could never show, because the payload reported one table.
 * It does not now: the `At the table` tab stays put and this names the tables inside the panel.
 *
 * Renders nothing below two tables. A switcher offering one choice is a label, and the table's
 * name is already in the page heading above it.
 *
 * The note about picks is not decoration. A Pull Sheet is device-local and keyed to one Event
 * (`usePullSheet`), so crossing to the other table leaves a half-built list behind at the first
 * one. A customer who finds that out by losing the list learns it too late.
 */
export function TableSwitcher(
  { tables, current, onChange }: {
    tables: readonly PublicLiveTable[];
    current: string;
    onChange: (eventId: string) => void;
  },
) {
  if (tables.length < 2) return null;
  return (
    <View testID="table-switcher" className="gap-1.5">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        // Bleeds past the header gutter to the screen edges, as the Location row does, so a table
        // cut off at the edge reads as more to scroll rather than as a clipped layout.
        className="-mx-4 grow-0"
        contentContainerClassName="gap-2 px-4"
      >
        {tables.map((table) => (
          <BrowsePill
            key={table.event_id}
            testID={`table-switcher-${table.event_id}`}
            label={table.name}
            accessibilityLabel={`Browse ${table.name}`}
            selected={table.event_id === current}
            // No toggle-off, unlike a Location pill: a customer is at a table, and "no table" is
            // not somewhere this page can stand.
            onPress={() => {
              if (table.event_id !== current) onChange(table.event_id);
            }}
          />
        ))}
      </ScrollView>
      <Text className="text-xs font-body text-muted-foreground">
        Your picks stay with the table you made them at.
      </Text>
    </View>
  );
}
